'use client';

import { twMerge } from 'tailwind-merge';
import { FC, useState, useCallback } from 'react';
import * as Select from '@radix-ui/react-select';

import { Svg } from '@/components';
import { ChevronDownIcon } from '@/icons';
import { wizzyGetVariantDataApi } from '@/utils/wizzyApis';
import { getKeyValue } from '@/utils/helper';
import { logger } from '@/config';

export type OptionTypes = {
  inStock: boolean;
  value: string;
  variationId: string;
};

export interface PropTypes {
  options: Array<OptionTypes> | [];
  defaultValue?: any;
  getSelected?: (...args: any) => void;
  item?: any;
  favourite?: boolean;
}

const SelectOptionProductCardWizzy: FC<PropTypes> = ({
  options = [],
  defaultValue = options[0],
  getSelected,
  item,
  favourite = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedPackageOption, selectPackageOption] =
    useState<OptionTypes | null>(defaultValue || null);

  const fetchVariantData = useCallback(
    async (variationId: string) => {
      try {
        setLoading(true);
        const response: any = await wizzyGetVariantDataApi({
          variationId: variationId,
          groupId: item?.groupId,
          includeOutOfStock: true,
        });
        if (response && !response?.noProductsFound && getSelected) {
          getSelected((prev: any) => {
            if (!prev) return prev;
            const customAttributes = getKeyValue(
              response?.custom_attributes,
              'attribute_code'
            );
            return {
              ...prev,
              name: response?.name,
              price: response?.price,
              sku: response?.sku,
              stock_status: response?.stock_status,
              custom_attributes: [
                {
                  attribute_code: 'weight',
                  value: customAttributes?.weight,
                },
                {
                  attribute_code: 'weight_unit',
                  value: customAttributes?.weight_unit,
                },
                {
                  attribute_code: 'url_key',
                  value: customAttributes?.url_key,
                },
                {
                  attribute_code: 'thumbnail',
                  value: customAttributes?.thumbnail,
                },
                ...(customAttributes?.special_price &&
                customAttributes?.special_price !== ''
                  ? [
                      {
                        attribute_code: 'special_price',
                        value: customAttributes.special_price,
                      },
                    ]
                  : []),
              ],
            };
          });
        }
      } catch (err) {
        logger.error('Error fetching variant data', err);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [item?.groupId]
  );

  const onOptionChange = useCallback(
    async (value: string) => {
      setLoading(true);
      const selectedOption = options.find(option => option.value === value);
      if (selectedOption) {
        await fetchVariantData(selectedOption.variationId);
        selectPackageOption(selectedOption);
      }
      setLoading(false);
    },
    [options]
  );

  if (loading) {
    return <div className='base-loading' />;
  }

  return (
    <div
      aria-label='options'
      className='relative'
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      {options && options?.length > 0 && options?.length === 1 ? (
        <p
          className={twMerge(
            'text-sm',
            favourite ? 'text-white' : 'text-black'
          )}>
          {options[0]?.value}
        </p>
      ) : options && options?.length > 0 ? (
        <Select.Root
          value={selectedPackageOption?.value}
          onValueChange={onOptionChange}>
          <Select.Trigger
            className={twMerge(
              'flex items-center gap-2 text-sm',
              favourite ? 'text-white' : 'text-black'
            )}>
            <Select.Value placeholder='Select an option' />
            <Svg
              viewBox='0 0 16 16'
              className={twMerge(
                'h-3 w-3 stroke-white',
                favourite ? 'stroke-white' : 'stroke-black'
              )}
              stroke='white'>
              <ChevronDownIcon />
            </Svg>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className='absolute right-0 top-6 z-30 max-h-22 w-max overflow-y-scroll rounded-md border border-fig bg-pale-rose hover:outline-hidden focus:outline-hidden'>
              <Select.Viewport>
                {options?.map(option => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    className={twMerge(
                      'w-full cursor-pointer px-3 py-2 text-sm',
                      !option?.inStock
                        ? 'opacity-30'
                        : 'hover:bg-fig/10 hover:text-fig'
                    )}>
                    <Select.ItemText>{option.value}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      ) : (
        <p className='text-sm text-black/60'>{options[0]?.value}</p>
      )}
    </div>
  );
};

export default SelectOptionProductCardWizzy;
